import React, { FC, useState } from 'react';
import Image from 'components/image/Image';
import Popup from 'components/shared/Popups/Popup/Index';
import Button from 'components/Button/Index';
import UpgradePlanPopup from 'components/shared/Popups/UpgradePlan/Index';
import { TFunction } from 'i18next';
import { loadStripe } from '@stripe/stripe-js';
import { useSnackbar } from 'notistack';

import './Index.scss';
import { SubscribtionPlansService } from 'services/subscribtionPlans/subscribtionPlans';
import { useSelector } from 'react-redux';
import { RootState } from 'redux/store';

interface PlanFeatures {
  jobs: number | 'unlimited';
  candidates: number;
  ai_credits: number;
}

export interface Plan {
  id: number;
  price: string;
  name: string;
  plan_id: string;
  product_id: string | null;
  currency: string;
  interval: string;
  live_mode: boolean;
  features: PlanFeatures;
}

interface PricingOptionProps {
  plan: Plan;
  className?: string;
  t: TFunction;
}

const PricingOption: FC<PricingOptionProps> = ({
  plan,
  className,
  t
}): JSX.Element => {
  const { companies } = useSelector((state: RootState) => state.company);
  const currentPlan = String(companies?.subscription?.plan || 'Free');
  const isActive = currentPlan === plan.name;
  const isFreeUser = currentPlan === 'Free';

  const [isUpgradePayment, setIsUpgradePayment] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const { enqueueSnackbar } = useSnackbar();

  const handleUpgradePayment = async (): Promise<void> => {
    try {
      setIsLoading(true);
      const response = await SubscribtionPlansService.getCheckoutSession(
        plan.plan_id
      );

      if (response?.url) {
        window.location.href = response.url;
        return;
      }

      if (response?.sessionId && response?.stripe_pub_key) {
        // Use Stripe.js to redirect to checkout
        const stripe = await loadStripe(response.stripe_pub_key);

        if (stripe) {
          const { error } = await stripe.redirectToCheckout({
            sessionId: response.sessionId
          });

          if (error) {
            console.error('Error redirecting to checkout:', error);
            enqueueSnackbar(
              t('Payment initialization failed. Please try again.'),
              {
                variant: 'error'
              }
            );
          }
        } else {
          console.error('Failed to initialize Stripe');
          enqueueSnackbar(
            t('Payment initialization failed. Please try again.'),
            {
              variant: 'error'
            }
          );
        }
      } else {
        console.error('No checkout data received from backend');
        enqueueSnackbar(t('Payment initialization failed. Please try again.'), {
          variant: 'error'
        });
      }
    } catch (error) {
      console.error('Error fetching checkout session:', error);
      enqueueSnackbar(t('Payment initialization failed. Please try again.'), {
        variant: 'error'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleManageSubscription = async (): Promise<void> => {
    try {
      setIsLoading(true);
      const response = await SubscribtionPlansService.getSubscriptionPortal();

      if (response?.url) {
        window.location.href = response.url;
      } else {
        enqueueSnackbar(
          t('Failed to access subscription portal. Please try again.'),
          {
            variant: 'error'
          }
        );
      }
    } catch (error) {
      console.error('Error accessing subscription portal:', error);
      enqueueSnackbar(
        t('Failed to access subscription portal. Please try again.'),
        {
          variant: 'error'
        }
      );
    } finally {
      setIsLoading(false);
    }
  };

  const formatPrice = (price: string): string => {
    return price === '0.0' ? t('€ 0') : `€ ${price}`;
  };

  const getButtonTitle = (): string => {
    if (isActive) {
      return t('Active');
    }

    if (currentPlan === 'Free') {
      return plan.name === 'Premium' ? t('Upgrade') : t('Get it');
    }

    if (currentPlan === 'Premium') {
      return plan.name === 'Pro' ? t('Upgrade') : '';
    }

    if (currentPlan === 'Pro') {
      return plan.name === 'Premium' ? t('Get it') : '';
    }

    return t('Get it');
  };

  const shouldShowButton = (): boolean => {
    // Only show buttons if user is on Free plan
    if (!isFreeUser) return false;

    if (plan.name === 'Free') return false;

    // No need for these checks since we already check if user is on Free plan
    // and we never show buttons for non-free users

    return true;
  };

  return (
    <>
      <div className={`option ${isActive ? 'active' : ''} ${className ?? ''}`}>
        <span className={`option-title ${isActive ? 'active-title' : ''} `}>
          {t(plan.name)}
        </span>
        <span className="option-price">{formatPrice(plan.price)}</span>
        {plan.price !== '0.0' && (
          <p className="option-month">/ {t(plan.interval)}</p>
        )}
        {plan.name === 'Free' && (
          <span className="option-attention">
            {t('Start hiring at no costs')}
          </span>
        )}
        {plan.name === 'Premium' && (
          <span className="option-attention">{t('Reach more talent')}</span>
        )}
        {plan.name === 'Pro' && (
          <span className="option-attention">
            {t('Maximize your hiring impact')}
          </span>
        )}
        <ul className="option__options">
          {plan.features && (
            <>
              <li className="option__options__list">
                <Image name="blue-check.svg" width={15} height={15} />
                <span className="option__options__list-option">
                  {plan?.features?.jobs === 'unlimited'
                    ? t('Unlimited Job posts (Fair Use Policy)')
                    : `${t('Create')} ${plan.features.jobs} ${t('job posts')}`
                  }
                </span>
              </li>
              <li className="option__options__list">
                <Image name="blue-check.svg" width={15} height={15} />
                <span className="option__options__list-option">
                  {t('Unlock')} {plan.features.candidates}{' '}
                  {t('candidate profiles')}
                </span>
              </li>
              <li className="option__options__list">
                <Image name="blue-check.svg" width={15} height={15} />
                <span className="option__options__list-option">
                  {plan.features.ai_credits} {t('AI credits')}
                </span>
              </li>
            </>
          )}
        </ul>
        {shouldShowButton() && (
          <Button
            className="option-button"
            t={t}
            title={getButtonTitle()}
            onClick={handleUpgradePayment}
            disabled={isLoading}
          />
        )}
      </div>
      {isUpgradePayment && (
        <Popup
          title={t('Upgrade your plan')}
          description={t(
            'Your subscription plan determines how many profiles you can unlock, how many new jobs you can post and how many AI credits you can use each month. Once you use up these credits, they won’t be replenished - even if you delete a job post. Any unused credits at the end of your billing cycle will roll over to the next month, so you don’t lose them.'
          )}
          onClose={() => setIsUpgradePayment(false)}
        >
          <UpgradePlanPopup t={t} selectedPlan={plan} />
        </Popup>
      )}
    </>
  );
};

export default PricingOption;
